import { engineHours, genHours, hullNm } from '../lib/utils';
import React, { useState, useEffect, useRef } from 'react';

// ── Shared primitives ─────────────────────────────────────────
function Lbl({ children, sub }) {
  return (
    <div style={{ marginBottom:4, minHeight:25 }}>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--slate)' }}>{children}</div>
      {sub && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', opacity:.7, marginTop:1 }}>{sub}</div>}
    </div>
  );
}

function SecHead({ children }) {
  return (
    <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--gold)', paddingBottom:6, marginBottom:10, borderBottom:'1px solid var(--border)' }}>
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'16px', marginBottom:14, ...style }}>
      {children}
    </div>
  );
}

function Field({ label, sub, children, half }) {
  return (
    <div style={{ marginBottom:12, gridColumn: half ? 'auto' : '1/-1' }}>
      <Lbl sub={sub}>{label}</Lbl>
      {children}
    </div>
  );
}

function TxtIn({ value, onChange, placeholder, type='text' }) {
  return <input type={type} value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder}/>;
}

function NumIn({ value, onChange, placeholder, step='.1' }) {
  return <input type="number" step={step} value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/>;
}

function DateIn({ value, onChange }) {
  return <input type="date" value={value||''} onChange={e=>onChange(e.target.value)}/>;
}

const ROLES = ['Captain','Crew','Tactician','Navigator','Trimmer','Bowman','Guest'];

const DEFAULTS = {
  engine_baseline:2940, engine_current:null, gen_baseline:1130, gen_current:null,
  hull_nm_baseline:12934,
  boat_name:'Pardon My French', boat_year:'2006', boat_make:'Jeanneau', boat_model:'Sun Odyssey 45',
  sail_number:'2590', phrf:'144', hailing_port:'Orcas, WA',
  mmsi:'', hin:'IRI00081G506', tow_us:'', syc_member:'',
  official_number:'1180998', build_year:'2005', place_built:'Les Herbiers, France',
  hull_material:'FRP (Fiberglass)', restrictions:'No fishery / no coastwise — foreign-built',
  loa:'45.0', beam:'14.3', depth:'9.5', gross_tonnage:'30', net_tonnage:'27',
  home_marina:'Elliott Bay Marina', slip:'D-4', home_lat:47.6234, home_lon:-122.3927,
  tank1_cap:90, tank2_cap:38, water_aft_cap:240, water_fore_cap:235,
  // Safety & Registration
  doc_uscg_number:'1180998', doc_uscg_expiry:'2027-09-30',
  doc_wa_reg_number:'1180998', doc_wa_reg_expiry:'2027-06-30', doc_wa_decal:'D588298',
  doc_insurance_company:'', doc_insurance_policy:'', doc_insurance_expiry:'',
  doc_canada_number:'', doc_canada_expiry:'',
  epirb_id:'', epirb_expiry:'', epirb_brand:'',
  liferaft_brand:'', liferaft_capacity:'', liferaft_service_expiry:'',
  plb_ids:'',
  // Crew
  crew_list:[{ id:'1', name:'Frederick', role:'Captain' }],
  default_captain:'Frederick',
  // Prefs
  theme:'system', temp_unit:'F',
};

const SECTION_TABS = [
  { id:'boat',    label:'Boat' },
  { id:'safety',  label:'Safety & Docs' },
  { id:'crew',    label:'Crew' },
  { id:'prefs',   label:'Preferences' },
];

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 760);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 760);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// Read-only value pulled from the logbook.
function Derived({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)' }}>{label}</div>
      <div style={{ background:'var(--tint, #FAF8F4)', border:'1px dashed var(--border)', borderRadius:8, padding:'12px 14px', marginTop:6 }}>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:18, fontWeight:600, color:'var(--navy)' }}>{value != null ? Number(value).toLocaleString() : '—'}</span>
      </div>
      {sub && <div style={{ fontSize:11, color:'var(--slate)', marginTop:5 }}>{sub}</div>}
    </div>
  );
}

const lastLogged = (entries, col) => {
  const rows = (entries||[]).filter(e => e[col] != null && e[col] !== '');
  if (!rows.length) return 'No logged readings yet';
  const top = rows.reduce((a,b) => Number(b[col]) > Number(a[col]) ? b : a);
  return `Highest logged ${top.date || ''}`.trim();
};

export default function SettingsModal({ isOpen, onClose, settings, entries = [], onSave, onUploadDoc }) {
  const [form, setForm]   = useState({});
  const [crew, setCrew]   = useState([]);
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [active, setActive] = useState('vessel');
  const [step, setStep]     = useState(0);
  const mobile    = useIsMobile();
  const secRefs   = useRef({});
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const m = { ...DEFAULTS, ...settings };
    setForm(m);
    setCrew(m.crew_list || DEFAULTS.crew_list);
    setPrefs({ theme: m.theme||'system', default_captain: m.default_captain||'Frederick', temp_unit: m.temp_unit||'F' });
    setStep(0);
    setActive('vessel');
  }, [isOpen, settings]);

  const set = (k,v) => setForm(f => ({ ...f, [k]:v }));

  const handleSave = async () => {
    setSaving(true);
    await onSave({ ...form, crew_list:crew, ...prefs });
    setSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  const sections = [
    { id:'vessel', label:'Vessel', node:(
      <Card>
        <SecHead>Vessel Identity</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="Boat Name"><TxtIn value={form.boat_name} onChange={v=>set('boat_name',v)} placeholder="Pardon My French"/></Field>
          <Field label="Model Year" half><TxtIn value={form.boat_year} onChange={v=>set('boat_year',v)} placeholder="2006"/></Field>
          <Field label="Make" half><TxtIn value={form.boat_make} onChange={v=>set('boat_make',v)} placeholder="Jeanneau"/></Field>
          <Field label="Model" half><TxtIn value={form.boat_model} onChange={v=>set('boat_model',v)} placeholder="Sun Odyssey 45"/></Field>
          <Field label="Sail Number" half><TxtIn value={form.sail_number} onChange={v=>set('sail_number',v)} placeholder="2590"/></Field>
          <Field label="PHRF Rating" half><TxtIn value={form.phrf} onChange={v=>set('phrf',v)} placeholder="144"/></Field>
          <Field label="Hailing Port" half><TxtIn value={form.hailing_port} onChange={v=>set('hailing_port',v)} placeholder="Orcas, WA"/></Field>
          <Field label="HIN" half><TxtIn value={form.hin} onChange={v=>set('hin',v)} placeholder="IRI00081G506"/></Field>
          <Field label="Official Number" sub="USCG" half><TxtIn value={form.official_number} onChange={v=>set('official_number',v)} placeholder="1180998"/></Field>
          <Field label="Build Year" sub="Hull completed" half><TxtIn value={form.build_year} onChange={v=>set('build_year',v)} placeholder="2005"/></Field>
          <Field label="Place Built" half><TxtIn value={form.place_built} onChange={v=>set('place_built',v)} placeholder="Les Herbiers, France"/></Field>
          <Field label="Hull Material" half><TxtIn value={form.hull_material} onChange={v=>set('hull_material',v)} placeholder="FRP (Fiberglass)"/></Field>
          <Field label="MMSI" half><TxtIn value={form.mmsi} onChange={v=>set('mmsi',v)} placeholder="338XXXXXX"/></Field>
          <Field label="Tow US / BoatUS #" half><TxtIn value={form.tow_us} onChange={v=>set('tow_us',v)} placeholder="XXX-XXXXX"/></Field>
          <Field label="SYC Member #" half><TxtIn value={form.syc_member} onChange={v=>set('syc_member',v)} placeholder="XXXX"/></Field>
          <Field label="Restrictions"><TxtIn value={form.restrictions} onChange={v=>set('restrictions',v)} placeholder="No fishery / no coastwise — foreign-built"/></Field>
        </div>
      </Card>
    ) },

    { id:'dimensions', label:'Dimensions', node:(
      <Card>
        <SecHead>Dimensions &amp; Tonnage</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="LOA (ft)" half><NumIn value={form.loa} onChange={v=>set('loa',v)} placeholder="45.0"/></Field>
          <Field label="Beam (ft)" half><NumIn value={form.beam} onChange={v=>set('beam',v)} placeholder="14.3"/></Field>
          <Field label="Depth (ft)" half><NumIn value={form.depth} onChange={v=>set('depth',v)} placeholder="9.5"/></Field>
          <Field label="Gross Tonnage" half><NumIn step="1" value={form.gross_tonnage} onChange={v=>set('gross_tonnage',v)} placeholder="30"/></Field>
          <Field label="Net Tonnage" half><NumIn step="1" value={form.net_tonnage} onChange={v=>set('net_tonnage',v)} placeholder="27"/></Field>
        </div>
      </Card>
    ) },

    { id:'marina', label:'Marina', node:(
      <Card>
        <SecHead>Home Marina</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="Marina"><TxtIn value={form.home_marina} onChange={v=>set('home_marina',v)} placeholder="Elliott Bay Marina"/></Field>
          <Field label="Slip" half><TxtIn value={form.slip} onChange={v=>set('slip',v)} placeholder="D-4"/></Field>
          <Field label="Latitude" sub="Used for weather auto-fill" half><NumIn step=".0001" value={form.home_lat} onChange={v=>set('home_lat',v)} placeholder="47.6234"/></Field>
          <Field label="Longitude" half><NumIn step=".0001" value={form.home_lon} onChange={v=>set('home_lon',v)} placeholder="-122.3927"/></Field>
        </div>
      </Card>
    ) },

    { id:'tanks', label:'Tanks', node:(
      <Card>
        <SecHead>Tank Capacities</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="Fuel Main (gal)" half><NumIn value={form.tank1_cap} onChange={v=>set('tank1_cap',v)} placeholder="90"/></Field>
          <Field label="Fuel Aux (gal)" half><NumIn value={form.tank2_cap} onChange={v=>set('tank2_cap',v)} placeholder="38"/></Field>
          <Field label="Water Aft (L)" half><NumIn value={form.water_aft_cap} onChange={v=>set('water_aft_cap',v)} placeholder="240"/></Field>
          <Field label="Water Fore (L)" half><NumIn value={form.water_fore_cap} onChange={v=>set('water_fore_cap',v)} placeholder="235"/></Field>
        </div>
      </Card>
    ) },

    { id:'engine', label:'Engine', node:(
      <Card>
        <SecHead>Engine &amp; Generator Baselines</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="Engine Baseline (hrs)" sub="Before the first logged trip" half><NumIn value={form.engine_baseline} onChange={v=>set('engine_baseline',v)} placeholder="2940"/></Field>
          <Field label="Generator Baseline (hrs)" sub="Before the first logged trip" half><NumIn value={form.gen_baseline} onChange={v=>set('gen_baseline',v)} placeholder="1130"/></Field>
          <Field label="Hull NM Baseline" sub="Before the first logged trip" half><NumIn value={form.hull_nm_baseline} onChange={v=>set('hull_nm_baseline',v)} placeholder="12934"/></Field>
        </div>
        <SecHead>Current Readings</SecHead>
        <div style={{ fontSize:12, color:'var(--slate)', marginBottom:10, lineHeight:1.5 }}>
          Read from the logbook, not typed. Engine and generator take the highest reading ever logged; hull distance is the baseline plus every logged trip.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Derived label="Engine (hrs)" value={engineHours(entries, form)} sub={lastLogged(entries,'engine_end')}/>
          <Derived label="Generator (hrs)" value={genHours(entries, form)} sub={lastLogged(entries,'gen_end')}/>
          <Derived label="Hull (nm)" value={hullNm(entries, form)} sub={`${entries.length} trips logged`}/>
        </div>
      </Card>
    ) },

    { id:'docs', label:'Documents', node:(<>
      <DocCard title="USCG Documentation"
        fields={[{ label:'Document Number', key:'doc_uscg_number', placeholder:'1180998' }]}
        expiryKey="doc_uscg_expiry" form={form} set={set} docType="uscg" onUpload={onUploadDoc}/>
      <DocCard title="WA State Registration"
        fields={[
          { label:'Registration Number', key:'doc_wa_reg_number', placeholder:'1180998' },
          { label:'Decal #', key:'doc_wa_decal', placeholder:'D588298' },
        ]}
        expiryKey="doc_wa_reg_expiry" form={form} set={set} docType="wa_reg" onUpload={onUploadDoc}/>
      <DocCard title="Vessel Insurance"
        fields={[
          { label:'Insurance Company', key:'doc_insurance_company', placeholder:'Markel / GEICO Marine' },
          { label:'Policy Number', key:'doc_insurance_policy', placeholder:'POL-XXXXXXX' },
        ]}
        expiryKey="doc_insurance_expiry" form={form} set={set} docType="insurance" onUpload={onUploadDoc}/>
      <DocCard title="Canadian Pleasure Craft Licence"
        fields={[{ label:'Licence Number', key:'doc_canada_number', placeholder:'CAN-XXXXX' }]}
        expiryKey="doc_canada_expiry" form={form} set={set} docType="canada" onUpload={onUploadDoc}/>
    </>) },

    { id:'safety', label:'Safety', node:(<>
      <Card>
        <SecHead>EPIRB</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="Brand / Model" half><TxtIn value={form.epirb_brand} onChange={v=>set('epirb_brand',v)} placeholder="ACR GlobalFix Pro"/></Field>
          <Field label="Hex ID" half><TxtIn value={form.epirb_id} onChange={v=>set('epirb_id',v)} placeholder="ADCXXXXXXXXXXXXXXX"/></Field>
          <Field label="Battery / Service Expiry" half><DateIn value={form.epirb_expiry} onChange={v=>set('epirb_expiry',v)}/></Field>
        </div>
      </Card>
      <Card>
        <SecHead>Liferaft</SecHead>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <Field label="Brand / Model" half><TxtIn value={form.liferaft_brand} onChange={v=>set('liferaft_brand',v)} placeholder="Plastimo 6-person"/></Field>
          <Field label="Capacity" half><TxtIn value={form.liferaft_capacity} onChange={v=>set('liferaft_capacity',v)} placeholder="6 persons"/></Field>
          <Field label="Service Expiry" half><DateIn value={form.liferaft_service_expiry} onChange={v=>set('liferaft_service_expiry',v)}/></Field>
        </div>
      </Card>
      <Card>
        <SecHead>PLBs / Other Safety</SecHead>
        <Field label="PLB IDs" sub="Comma-separated HEX IDs"><TxtIn value={form.plb_ids} onChange={v=>set('plb_ids',v)} placeholder="ACR ResQLink+ × 3"/></Field>
      </Card>
    </>) },

    { id:'crew', label:'Crew', node:(<>
      <Card>
        <SecHead>Default Captain</SecHead>
        <Field label="Name" sub="Used as default captain on new log entries">
          <TxtIn value={prefs.default_captain} onChange={v=>setPrefs(p=>({...p,default_captain:v}))} placeholder="Frederick"/>
        </Field>
      </Card>
      <Card>
        <SecHead>Regular Crew</SecHead>
        {crew.map(m => (
          <div key={m.id} style={{ display:'grid', gridTemplateColumns:'1fr minmax(100px, 140px) 32px', gap:8, marginBottom:8, alignItems:'end' }}>
            <div>
              {crew.indexOf(m)===0 && <Lbl>Name</Lbl>}
              <input type="text" value={m.name||''} onChange={e=>setCrew(c=>c.map(x=>x.id===m.id?{...x,name:e.target.value}:x))} placeholder="Name"/>
            </div>
            <div>
              {crew.indexOf(m)===0 && <Lbl>Role</Lbl>}
              <select value={m.role||'Crew'} onChange={e=>setCrew(c=>c.map(x=>x.id===m.id?{...x,role:e.target.value}:x))}>
                {ROLES.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
            <button onClick={()=>setCrew(c=>c.filter(x=>x.id!==m.id))} style={{ background:'none', border:'none', color:'var(--slate)', fontSize:16, cursor:'pointer', paddingBottom:crew.indexOf(m)===0?20:0 }}>✕</button>
          </div>
        ))}
        <button onClick={()=>setCrew(c=>[...c,{id:Date.now().toString(),name:'',role:'Crew'}])}
          style={{ width:'100%', marginTop:4, padding:'9px', borderRadius:7, border:'1.5px dashed var(--border)', background:'transparent', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--slate)', cursor:'pointer' }}>
          + Add Crew Member
        </button>
      </Card>
    </>) },

    { id:'prefs', label:'Preferences', node:(<>
      <Card>
        <SecHead>Appearance</SecHead>
        <div style={{ display:'flex', gap:8 }}>
          {[['light','☀','Light'],['dark','🌙','Dark'],['system','⚙','System']].map(([v,icon,l])=>(
            <button key={v} onClick={()=>setPrefs(p=>({...p,theme:v}))} style={{ flex:1, padding:'12px 8px', borderRadius:9, cursor:'pointer', textAlign:'center', border: prefs.theme===v?'2px solid var(--navy)':'1px solid var(--border)', background:prefs.theme===v?'var(--navy)':'var(--surface)', color:prefs.theme===v?'var(--gold-lt)':'var(--slate)' }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{icon}</div>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em' }}>{l}</div>
            </button>
          ))}
        </div>
      </Card>
      <Card>
        <SecHead>Temperature Unit</SecHead>
        <div style={{ display:'flex', gap:8 }}>
          {[['F','°F Fahrenheit'],['C','°C Celsius']].map(([v,l])=>(
            <button key={v} onClick={()=>setPrefs(p=>({...p,temp_unit:v}))} style={{ flex:1, padding:'10px', borderRadius:7, cursor:'pointer', fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700, border:prefs.temp_unit===v?'2px solid var(--navy)':'1px solid var(--border)', background:prefs.temp_unit===v?'var(--navy)':'var(--surface)', color:prefs.temp_unit===v?'var(--gold-lt)':'var(--slate)' }}>{l}</button>
          ))}
        </div>
      </Card>
      <Card>
        <SecHead>About</SecHead>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:'var(--slate)', lineHeight:1.8 }}>
          PMF Logbook · v2.0<br/>
          S/V Pardon My French · Elliott Bay Marina<br/>
          Built with Supabase · Anthropic
        </div>
      </Card>
    </>) },
  ];

  const goTo = (id) => {
    setActive(id);
    const el = secRefs.current[id], c = scrollRef.current;
    if (el && c) c.scrollTo({ top: Math.max(el.offsetTop - 8, 0), behavior:'smooth' });
  };
  const onScrollSpy = () => {
    const c = scrollRef.current;
    if (!c) return;
    const y = c.scrollTop + 100;
    let cur = sections[0].id;
    for (const s of sections) { const el = secRefs.current[s.id]; if (el && el.offsetTop <= y) cur = s.id; }
    setActive(cur);
  };

  const btnOutline = { fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, padding:'10px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--slate)', cursor:'pointer' };
  const btnPrimary = { fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:700, padding:'10px 22px', borderRadius:7, border:'none', background:'linear-gradient(135deg,var(--navy),#1d3e63)', color:'var(--gold-lt)', cursor:'pointer', opacity:saving?.7:1 };

  // ── MOBILE — stepped sections, tap a chip to jump, save anytime ──
  if (mobile) {
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:400, display:'flex', alignItems:'flex-end', justifyContent:'center' }} onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div style={{ background:'var(--bg)', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:640, height:'94vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,.35)', overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:'var(--surface)' }}>
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'var(--slate)', cursor:'pointer' }}>✕</button>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--navy)' }}>Settings</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:600, color:'var(--gold)' }}>{step+1} / {sections.length}</div>
          </div>
          <div style={{ display:'flex', gap:6, padding:'10px 16px', overflowX:'auto', borderBottom:'1px solid var(--border)', flexShrink:0, background:'var(--surface)' }}>
            {sections.map((s,i)=>(
              <button key={s.id} onClick={()=>setStep(i)} style={{ flex:'0 0 auto', padding:'6px 0', marginRight:16, background:'none', border:'none', cursor:'pointer', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color: step===i?'var(--navy)':'var(--slate)', borderBottom: step===i?'2px solid var(--gold)':'2px solid transparent', whiteSpace:'nowrap' }}>{s.label}</button>
            ))}
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>
            {sections[step].node}
          </div>
          <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', gap:10, flexShrink:0, background:'var(--surface)', paddingBottom:'max(12px, env(safe-area-inset-bottom))' }}>
            {step < sections.length-1 && <button onClick={()=>setStep(s=>s+1)} style={{ ...btnOutline, flex:'0 0 auto' }}>Next</button>}
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, flex:1 }}>{saving ? 'Saving…' : 'Save settings'}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── DESKTOP — section rail + card content ──
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'var(--bg)', borderRadius:16, width:'100%', maxWidth:980, height:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,.35)', overflow:'hidden' }}>
        <div style={{ padding:'15px 22px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:'var(--surface)' }}>
          <div style={{ fontSize:18, fontWeight:700, color:'var(--navy)' }}>Settings</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, color:'var(--slate)', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'190px 1fr', flex:1, minHeight:0 }}>
          <aside style={{ background:'var(--surface)', borderRight:'1px solid var(--border)', padding:'14px 10px', overflowY:'auto' }}>
            {sections.map(s=>(
              <button key={s.id} onClick={()=>goTo(s.id)} style={{ display:'block', width:'100%', textAlign:'left', padding:'9px 12px', marginBottom:2, borderRadius:8, border:'none', cursor:'pointer', fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', background: active===s.id?'var(--navy)':'transparent', color: active===s.id?'var(--gold-lt)':'var(--slate)' }}>{s.label}</button>
            ))}
          </aside>
          <div ref={scrollRef} onScroll={onScrollSpy} style={{ overflowY:'auto', padding:'16px 22px', position:'relative' }}>
            {sections.map(s=>(
              <div key={s.id} ref={el=>{ secRefs.current[s.id]=el; }} style={{ marginBottom:4 }}>
                {s.node}
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding:'12px 22px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0, background:'var(--surface)' }}>
          <button onClick={onClose} style={btnOutline}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save settings'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Document card with upload + expiry ────────────────────────
function DocCard({ title, fields, expiryKey, form, set, docType, onUpload }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const urlKey = `doc_${docType}_url`;
  const expired = form[expiryKey] && new Date(form[expiryKey]) < new Date();
  const daysLeft = form[expiryKey] ? Math.ceil((new Date(form[expiryKey]) - new Date()) / 86400000) : null;
  const soon = daysLeft !== null && daysLeft >= 0 && daysLeft < 90;

  const handleUpload = async (file) => {
    if (!file || !onUpload) return;
    setUploading(true);
    try {
      const url = await onUpload(file, docType);
      if (url) set(urlKey, url);
    } catch {}
    setUploading(false);
  };

  const statusColor = expired ? 'var(--red)' : soon ? '#D97706' : 'var(--green)';
  const statusLabel = expired ? 'EXPIRED' : soon ? `${daysLeft}d left` : daysLeft !== null ? 'Valid' : null;

  return (
    <Card>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <SecHead>{title}</SecHead>
        {statusLabel && (
          <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, color:statusColor, background:`${statusColor}15`, padding:'2px 8px', borderRadius:4, flexShrink:0, marginLeft:8 }}>{statusLabel}</span>
        )}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
        {fields.map(f => (
          <Field key={f.key} label={f.label} half>
            <TxtIn value={form[f.key]} onChange={v=>set(f.key,v)} placeholder={f.placeholder}/>
          </Field>
        ))}
        <Field label="Expiry Date" half>
          <input type="date" value={form[expiryKey]||''} onChange={e=>set(expiryKey,e.target.value)}
            style={{ borderColor: expired?'var(--red)':soon?'#D97706':'var(--border)' }}/>
        </Field>
      </div>
      <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:10 }}>
        <input ref={ref} type="file" accept=".pdf,image/*" style={{ display:'none' }} onChange={e=>e.target.files[0]&&handleUpload(e.target.files[0])}/>
        <button onClick={()=>ref.current.click()} disabled={uploading} style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', padding:'6px 12px', borderRadius:6, border:'1px solid var(--border)', background:'var(--s2)', color:'var(--slate)', cursor:'pointer' }}>
          {uploading ? '↑ Uploading…' : form[urlKey] ? '✓ Uploaded · Replace' : '📄 Upload PDF / Photo'}
        </button>
        {form[urlKey] && (
          <a href={form[urlKey]} target="_blank" rel="noreferrer" style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--gold)', textDecoration:'none' }}>View ↗</a>
        )}
      </div>
    </Card>
  );
}
